/**
 * API Service - Centralized API calls for AfriRepWatch
 * With built-in caching to avoid redundant requests
 */
import axios from 'axios';
import { getCached, setCache, invalidateCache, invalidateBrandCache } from './cache';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Cache TTLs (in milliseconds)
const CACHE_TTL = {
  SHORT: 2 * 60 * 1000,     // 2 minutes - for frequently changing data
  MEDIUM: 5 * 60 * 1000,    // 5 minutes - default
  LONG: 15 * 60 * 1000,     // 15 minutes - for AI-generated content
  VERY_LONG: 30 * 60 * 1000, // 30 minutes - for static data
  DAY: 24 * 60 * 60 * 1000, // 24 hours - for cron-refreshed data (SERP, Expert RP)
};

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Retry interceptor for cold-start timeouts (Render free tier)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    // Retry once on timeout (cold start) — skip if already retried
    if (error.code === 'ECONNABORTED' && !config._retried) {
      config._retried = true;
      config.timeout = 90000; // 90s for cold start retry
      console.log('[API] Serveur en cours de réveil, nouvelle tentative...');
      // Dispatch event so UI can show a message
      window.dispatchEvent(new CustomEvent('api:coldstart'));
      return api(config);
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    if (error.response?.status === 429) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'object' ? detail.message : detail || 'Limite quotidienne atteinte.';
      window.dispatchEvent(new CustomEvent('api:quota_exceeded', { detail: { message } }));
    }
    return Promise.reject(error);
  }
);

/**
 * Helper: Fetch with cache
 * Checks cache first, fetches if not cached, stores result
 */
async function fetchWithCache(cacheKey, params, fetchFn, ttl = CACHE_TTL.DAY, forceRefresh = false) {
  // Check cache first (unless forcing refresh)
  if (!forceRefresh) {
    const cached = getCached(cacheKey, params);
    if (cached) {
      return { data: cached, fromCache: true };
    }
  }

  // Fetch fresh data
  const response = await fetchFn();

  // Cache the successful result
  if (response?.data) {
    setCache(cacheKey, params, response.data, ttl);
  }

  return response;
}

// === Auth API ===
export const authAPI = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  register: (data) => api.post('/api/auth/register', data),
  me: () => api.get('/api/auth/me'),
};

// === Monitoring API ===
export const monitoringAPI = {
  search: (keywords, brandId = null, saveResults = true, distributeHistory = false) =>
    api.post('/api/monitoring/search', {
      keywords,
      brand_id: brandId,
      save_results: saveResults,
      distribute_history: distributeHistory
    }, {
      timeout: 300000  // 5 minutes - scraping multiple sources takes time!
    }),

  // Mentions - WITH CACHE for dashboard performance
  getMentions: (params, forceRefresh = false) =>
    fetchWithCache(
      'monitoring/mentions',
      params,
      () => api.get('/api/monitoring/mentions', { params }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  getMentionStats: (brandId, days = 7) =>
    fetchWithCache(
      'monitoring/mentions/stats',
      { brand_id: brandId, days },
      () => api.get('/api/monitoring/mentions/stats', { params: { brand_id: brandId, days } }),
      CACHE_TTL.DAY
    ),

  // Brands - MEDIUM cache (changes rarely)
  getBrands: (forceRefresh = false) =>
    fetchWithCache(
      'monitoring/brands',
      {},
      () => api.get('/api/monitoring/brands'),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  createBrand: (name, keywords, sector = 'autre', profile_type = 'organisation') => {
    return api.post('/api/monitoring/brands', { name, keywords, sector, profile_type }).then(response => {
      // Invalidate brands cache
      invalidateBrandCache('brands');
      return response;
    });
  },

  updateBrandAISummary: (brandId, aiSummary) =>
    api.put(`/api/monitoring/brands/${brandId}/ai-summary`, { ai_summary: aiSummary }),

  updateSocialUrls: (brandId, socialUrls) =>
    api.put(`/api/monitoring/brands/${brandId}/social-urls`, { social_urls: socialUrls }).then(response => {
      invalidateBrandCache('brands');
      return response;
    }),

  deleteBrand: (brandId) => {
    return api.delete(`/api/monitoring/brands/${brandId}`).then(response => {
      invalidateBrandCache(brandId);
      return response;
    });
  },

  collect: (brandId, platform = 'twitter') =>
    api.post('/api/monitoring/collect', null, { params: { brand_id: brandId, platform } }),

  // Apify multi-platform collection
  getApifyPlatforms: () =>
    api.get('/api/monitoring/apify/platforms'),

  collectApify: (brandId, platforms = null, maxPerPlatform = 30, brandUrls = null) =>
    api.post(`/api/monitoring/collect-apify/${brandId}`, {
      platforms,
      max_per_platform: maxPerPlatform,
      brand_urls: brandUrls,
    }, { timeout: 300000 }),  // 5 minutes - Apify actors can take time

  validateApifyToken: () => api.get('/api/monitoring/apify/validate'),

  // Unified collect-all: triggers ALL sources (RSS, Google News, Opera News, Facebook, Twitter, Perplexity, Apify)
  collectAll: (brandId, options = {}) =>
    api.post(`/api/monitoring/collect-all/${brandId}`, {
      days_lookback: options.daysLookback || 15,
      include_rss: options.includeRss !== false,
      include_twitter: options.includeTwitter !== false,
      include_perplexity: options.includePerplexity !== false,
      include_apify: options.includeApify !== false,
      brand_urls: options.brandUrls || null,
      max_per_source: options.maxPerSource || 30,
    }, { timeout: 600000 }),  // 10 minutes - all sources take time

  // Cleanup: re-evaluate existing mentions and delete irrelevant ones
  cleanup: (brandId) =>
    api.post(`/api/monitoring/cleanup/${brandId}`, {}, { timeout: 120000 }),

  // Re-analyze sentiments for all mentions using Claude Haiku
  reanalyzeSentiments: (brandId) =>
    api.post(`/api/monitoring/reanalyze-sentiments/${brandId}`, {}, { timeout: 300000 }),

  // Update brand profile (name, keywords, type, sector, partner_keywords)
  updateBrandProfile: (brandId, profileData) =>
    api.put(`/api/monitoring/brands/${brandId}/profile`, profileData).then(response => {
      invalidateBrandCache('brands');
      return response;
    }),

  // Brand images
  fetchBrandImages: async (brandId, force = false) => {
    const response = await api.post(`/api/monitoring/images/fetch/${brandId}?force=${force}`, {}, { timeout: 120000 });
    // Invalidate image cache so all pages get fresh photo_urls
    invalidateCache(`monitoring/images/${brandId}`);
    return response;
  },
  getBrandImages: (brandId, forceRefresh = false) =>
    fetchWithCache(
      `monitoring/images/${brandId}`,
      {},
      () => api.get(`/api/monitoring/images/${brandId}`),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  getBenchmark: (brandId, forceRefresh = false) =>
    fetchWithCache(
      `benchmark/comparison/${brandId}`,
      {},
      () => api.get(`/api/benchmark/comparison/${brandId}`),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  uploadBrandPhoto: (brandId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/monitoring/brands/${brandId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  // Cleanup irrelevant mentions (re-evaluates with current keywords)
  cleanupMentions: (brandId) =>
    api.post(`/api/monitoring/cleanup/${brandId}`, {}, { timeout: 60000 }),
};

// === Analytics API ===
export const analyticsAPI = {
  getDashboard: (brandId, days = 7) =>
    api.get('/api/analytics/dashboard', { params: { brand_id: brandId, days } }),

  getTimeline: (brandId, days = 7) =>
    api.get('/api/analytics/timeline', { params: { brand_id: brandId, days } }),

  getSources: (brandId, days = 7) =>
    api.get('/api/analytics/sources', { params: { brand_id: brandId, days } }),

  getRecentMentions: (params) =>
    api.get('/api/analytics/mentions/recent', { params }),

  getInfluencers: (brandId, days = 7, limit = 10) =>
    api.get('/api/analytics/influencers', { params: { brand_id: brandId, days, limit } }),

  getCriticalAlerts: (brandId, days = 7) =>
    api.get('/api/analytics/alerts/critical', { params: { brand_id: brandId, days } }),

  analyzeSentiment: (text, language) =>
    api.post('/api/analytics/analyze', null, { params: { text, language } }),

  getLatestNews: (keywords, limit = 20) =>
    api.get('/api/analytics/news/latest', { params: { keywords, limit } }),

  getNewsSources: () => api.get('/api/analytics/news/sources'),

  // New endpoints for UX Pro v2
  getToday: (brandId, forceRefresh = false) =>
    fetchWithCache(
      'analytics/today',
      { brand_id: brandId },
      () => api.get('/api/analytics/today', { params: { brand_id: brandId } }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  getHistory: (brandId, days = 30) =>
    fetchWithCache(
      'analytics/history',
      { brand_id: brandId, days },
      () => api.get('/api/analytics/history', { params: { brand_id: brandId, days } }),
      CACHE_TTL.DAY
    ),

  comparePeriods: (brandId, period1Days = 7, period2Days = 7) =>
    api.get('/api/analytics/history/compare', {
      params: { brand_id: brandId, period1_days: period1Days, period2_days: period2Days }
    }),

  // Monthly analytics endpoints (v2) - WITH CACHING
  getMonthlyMetrics: (brandId, yearMonth = null, forceRefresh = false) =>
    fetchWithCache(
      `analytics/monthly/${brandId}`,
      { year_month: yearMonth },
      () => api.get(`/api/analytics/monthly/${brandId}`, {
        params: yearMonth ? { year_month: yearMonth } : {}
      }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  getMonthlyHistory: (brandId, months = 6) =>
    fetchWithCache(
      `analytics/monthly/${brandId}/history`,
      { months },
      () => api.get(`/api/analytics/monthly/${brandId}/history`, { params: { months } }),
      CACHE_TTL.DAY
    ),

  getMonthlyTopics: (brandId, yearMonth = null, topicType = null, limit = 10, forceRefresh = false) =>
    fetchWithCache(
      `analytics/monthly/${brandId}/topics`,
      { year_month: yearMonth, topic_type: topicType, limit },
      () => api.get(`/api/analytics/monthly/${brandId}/topics`, {
        params: {
          ...(yearMonth && { year_month: yearMonth }),
          ...(topicType && { topic_type: topicType }),
          limit
        }
      }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  getMonthlyActions: (brandId, yearMonth = null, status = null, priority = null, limit = 20, forceRefresh = false) =>
    fetchWithCache(
      `analytics/monthly/${brandId}/actions`,
      { year_month: yearMonth, status, priority, limit },
      () => api.get(`/api/analytics/monthly/${brandId}/actions`, {
        params: {
          ...(yearMonth && { year_month: yearMonth }),
          ...(status && { status }),
          ...(priority && { priority }),
          limit
        }
      }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  calculateMonthlyMetrics: (brandId, yearMonth = null, force = false) => {
    // After calculation, invalidate cache for this brand
    return api.post(`/api/analytics/monthly/${brandId}/calculate`, null, {
      params: {
        ...(yearMonth && { year_month: yearMonth }),
        force
      },
      timeout: 60000
    }).then(response => {
      // Invalidate cache after successful calculation
      invalidateBrandCache(brandId);
      return response;
    });
  },

  // AI Interpretations - LONG CACHE (expensive to generate)
  getMonthlyInterpretations: (brandId, yearMonth = null, forceRefresh = false) =>
    fetchWithCache(
      `analytics/monthly/${brandId}/interpretations`,
      { year_month: yearMonth },
      () => api.get(`/api/analytics/monthly/${brandId}/interpretations`, {
        params: yearMonth ? { year_month: yearMonth } : {},
        timeout: 30000
      }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  // Drivers interpretations - NEW endpoint for contextual AI text
  getDriversInterpretations: (brandId, yearMonth = null, forceRefresh = false) =>
    fetchWithCache(
      `analytics/monthly/${brandId}/drivers-interpretations`,
      { year_month: yearMonth },
      () => api.get(`/api/analytics/monthly/${brandId}/drivers-interpretations`, {
        params: yearMonth ? { year_month: yearMonth } : {},
        timeout: 30000
      }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  updateActionStatus: (actionId, status, assignedTo = null, notes = null) =>
    api.put(`/api/analytics/actions/${actionId}/status`, null, {
      params: { status, assigned_to: assignedTo, notes }
    }),
};

// === Crisis API ===
export const crisisAPI = {
  // Active crisis - SHORT cache (important to be fresh)
  getActive: (brandId = null, forceRefresh = false) =>
    fetchWithCache(
      'crisis/active',
      { brand_id: brandId },
      () => api.get('/api/crisis/active', { params: { brand_id: brandId } }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  getCurrent: (brandId, forceRefresh = false) =>
    fetchWithCache(
      'crisis/current',
      { brand_id: brandId },
      () => api.get('/api/crisis/current', { params: { brand_id: brandId } }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  getDetails: (crisisId) =>
    api.get(`/api/crisis/${crisisId}`),

  getHistory: (brandId = null, days = 90) =>
    fetchWithCache(
      'crisis/history',
      { brand_id: brandId, days },
      () => api.get('/api/crisis/history', { params: { brand_id: brandId, days } }),
      CACHE_TTL.DAY
    ),

  resolve: (crisisId, notes = null, effectiveness = null) => {
    return api.post(`/api/crisis/${crisisId}/resolve`, { notes, effectiveness }).then(response => {
      // Invalidate crisis cache
      invalidateBrandCache('crisis');
      return response;
    });
  },

  addAction: (crisisId, action, userId = null) =>
    api.post(`/api/crisis/${crisisId}/action`, { action, user_id: userId }),

  analyze: (crisisId) =>
    api.post(`/api/crisis/${crisisId}/analyze`),

  getRecommendations: (crisisId) =>
    api.get(`/api/crisis/${crisisId}/recommendations`),

  createManual: (brandId, severity, description, mentionIds = null) => {
    return api.post('/api/crisis/manual', {
      brand_id: brandId,
      severity,
      description,
      mention_ids: mentionIds
    }).then(response => {
      invalidateBrandCache(brandId);
      return response;
    });
  },

  triggerCheck: (brandId) =>
    api.post(`/api/crisis/check/${brandId}`),

  getTimeline: (crisisId) => api.get(`/api/crisis/${crisisId}/timeline`),
  getReport: (crisisId) => api.get(`/api/crisis/${crisisId}/report`),
};

// === Objectives API ===
export const objectivesAPI = {
  // Metadata - VERY_LONG cache (rarely changes)
  getTypes: () =>
    fetchWithCache('objectives/types', {}, () => api.get('/api/objectives/types'), CACHE_TTL.DAY),
  getHorizons: () =>
    fetchWithCache('objectives/horizons', {}, () => api.get('/api/objectives/horizons'), CACHE_TTL.DAY),
  getSectors: () =>
    fetchWithCache('objectives/sectors', {}, () => api.get('/api/objectives/sectors'), CACHE_TTL.DAY),

  // Brand analysis (for wizard) - instant from sector + profile benchmarks
  analyzeBrand: (name, keywords, sector, profileType = 'organisation') =>
    api.post('/api/objectives/analyze-brand', {
      name, keywords, sector, profile_type: profileType,
    }),

  // Search social media URLs (wizard magic auto-fill)
  searchSocialUrls: (name, sector, profileType = 'organisation') =>
    api.post('/api/objectives/search-social-urls', {
      name, sector, profile_type: profileType,
    }),

  // Brand objectives CRUD - WITH CACHING
  getBrandObjectives: (brandId, activeOnly = true, forceRefresh = false) =>
    fetchWithCache(
      `objectives/brand/${brandId}`,
      { active_only: activeOnly },
      () => api.get(`/api/objectives/brand/${brandId}`, { params: { active_only: activeOnly } }),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  create: (data) => {
    return api.post('/api/objectives', data).then(response => {
      invalidateBrandCache(data.brand_id);
      return response;
    });
  },

  createBulk: (brandId, objectives) => {
    return api.post('/api/objectives/bulk', { brand_id: brandId, objectives }).then(response => {
      invalidateBrandCache(brandId);
      return response;
    });
  },

  update: (objectiveId, data) =>
    api.put(`/api/objectives/${objectiveId}`, data),

  delete: (objectiveId) =>
    api.delete(`/api/objectives/${objectiveId}`),

  // Lifecycle
  pause: (objectiveId) => {
    return api.post(`/api/objectives/${objectiveId}/pause`).then(response => {
      invalidateCache('objectives');
      return response;
    });
  },

  resume: (objectiveId) => {
    return api.post(`/api/objectives/${objectiveId}/resume`).then(response => {
      invalidateCache('objectives');
      return response;
    });
  },

  extend: (objectiveId, data) => {
    return api.post(`/api/objectives/${objectiveId}/extend`, data).then(response => {
      invalidateCache('objectives');
      return response;
    });
  },

  close: (objectiveId, data) => {
    return api.post(`/api/objectives/${objectiveId}/close`, data).then(response => {
      invalidateCache('objectives');
      return response;
    });
  },

  getHistory: (brandId) =>
    fetchWithCache(
      `objectives/brand/${brandId}/history`,
      {},
      () => api.get(`/api/objectives/brand/${brandId}/history`),
      CACHE_TTL.DAY
    ),

  // Progress tracking - WITH CACHING
  getProgress: (brandId, forceRefresh = false) =>
    fetchWithCache(
      `objectives/progress/${brandId}`,
      {},
      () => api.get(`/api/objectives/progress/${brandId}`),
      CACHE_TTL.DAY,
      forceRefresh
    ),

  // Legacy endpoints
  getLegacy: (brandId, userId = null) =>
    api.get('/api/objectives/legacy', { params: { brand_id: brandId, user_id: userId } }),

  getRecommendations: (brandId) =>
    api.get('/api/objectives/recommendations', { params: { brand_id: brandId } }),
};

// === Alerts API ===
export const alertsAPI = {
  getAlerts: () => api.get('/api/alerts'),
  createAlert: (data) => api.post('/api/alerts', data),
  deleteAlert: (alertId) => api.delete(`/api/alerts/${alertId}`),
};

// === Reports API ===
export const reportsAPI = {
  generate: (brandId, date = null) =>
    api.post('/api/reports/generate', { brand_id: brandId, date }),

  getLatest: (brandId) =>
    fetchWithCache(
      'reports/latest',
      { brand_id: brandId },
      () => api.get('/api/reports/latest', { params: { brand_id: brandId } }),
      CACHE_TTL.DAY
    ),

  getHistory: (brandId, days = 30, limit = 30) =>
    fetchWithCache(
      `reports/history/${brandId}`, { days, limit },
      () => api.get('/api/reports/history', { params: { brand_id: brandId, days, limit } }),
      CACHE_TTL.DAY
    ),

  getById: (reportId) =>
    api.get(`/api/reports/${reportId}`),

  downloadPdf: (reportId) =>
    api.get(`/api/reports/${reportId}/pdf`, { responseType: 'blob' }),

  // Dashboard summary - LONG CACHE (AI generated)
  getDashboardSummary: (brandId, refreshAI = false) => {
    // If refreshAI is true, bypass cache and force fetch
    if (refreshAI) {
      return api.get('/api/reports/dashboard/summary', {
        params: { brand_id: brandId, refresh_ai: true },
        timeout: 60000
      }).then(response => {
        // Update cache with new data
        setCache('reports/dashboard/summary', { brand_id: brandId }, response.data, CACHE_TTL.DAY);
        return response;
      });
    }

    return fetchWithCache(
      'reports/dashboard/summary',
      { brand_id: brandId },
      () => api.get('/api/reports/dashboard/summary', {
        params: { brand_id: brandId, refresh_ai: false },
        timeout: 30000
      }),
      CACHE_TTL.DAY
    );
  },

  // Editable report preview
  getPreview: (brandId) =>
    api.get(`/api/reports/preview/${brandId}`, { timeout: 30000 }),

  // Generate PDF from edited sections
  generateEditablePdf: (brandId, data) =>
    api.post(`/api/reports/generate-pdf/${brandId}`, data, {
      responseType: 'blob',
      timeout: 60000,
    }),

  // Save edited sections to a report
  saveSections: (reportId, sections, comments = null) =>
    api.patch(`/api/reports/${reportId}/sections`, { sections, comments }),

  // Send report to client (admin review flow)
  sendToClient: (reportId) =>
    api.post(`/api/reports/${reportId}/send`),
};

// === Budget / AI Usage API ===
export const budgetAPI = {
  getAiBudget: (period = '30d') =>
    fetchWithCache(
      `settings/ai-budget/${period}`,
      {},
      () => api.get('/api/settings/ai-budget', { params: { period } }),
      CACHE_TTL.DAY
    ),
};

// === SEO & Influence API ===
export const seoAPI = {
  // SERP Analysis (DAY cache — refreshed by cron/Actualiser only)
  getSerpResults: (brandId, keyword = null, forceRefresh = false) =>
    forceRefresh
      ? api.get(`/api/seo/serp/${brandId}`, { params: keyword ? { keyword } : {} }).then(r => {
          // Update cache with fresh data
          try { const k = `seo/serp/${brandId}_${JSON.stringify({ keyword })}`; localStorage.setItem(k, JSON.stringify({ data: r.data, ts: Date.now() })); } catch(e) {}
          return r;
        })
      : fetchWithCache(
          `seo/serp/${brandId}`,
          { keyword },
          () => api.get(`/api/seo/serp/${brandId}`, { params: keyword ? { keyword } : {} }),
          CACHE_TTL.DAY
        ),

  scanSerp: (brandId, keyword = null, force = false) =>
    api.post(`/api/seo/serp/${brandId}/scan`, null, {
      params: { ...(keyword ? { keyword } : {}), ...(force ? { force: true } : {}) },
      timeout: 120000  // 2 minutes - Perplexity can be slow
    }),

  // Campaigns
  getCampaigns: (brandId, status = null) =>
    fetchWithCache(
      `seo/campaigns/${brandId}`,
      { status },
      () => api.get(`/api/seo/campaigns/${brandId}`, { params: status ? { status } : {} }),
      CACHE_TTL.DAY
    ),

  createCampaign: (brandId, data) =>
    api.post(`/api/seo/campaigns/${brandId}`, null, {
      params: {
        name: data.name,
        campaign_type: data.campaign_type,
        target_keyword: data.target_keyword,
        target_url: data.target_url || undefined,
        notes: data.notes || undefined,
      }
    }),

  updateCampaignStatus: (campaignId, status) =>
    api.put(`/api/seo/campaigns/${campaignId}/status`, null, {
      params: { status }
    }),

  // Content Generation & Publishing
  generateContent: (brandId, topic, contentType = 'article', tone = 'professionnel') =>
    api.post('/api/seo/content/generate', null, {
      params: { brand_id: brandId, topic, content_type: contentType, tone },
      timeout: 120000  // 2 minutes for AI generation
    }),

  publishContent: (brandId, platform, content, title = null, contentType = 'post', campaignId = null) =>
    api.post('/api/seo/content/publish', null, {
      params: {
        brand_id: brandId,
        platform,
        content,
        title: title || undefined,
        content_type: contentType,
        campaign_id: campaignId || undefined,
      }
    }),

  getPublishedContent: (brandId, status = null) =>
    fetchWithCache(
      `seo/content/${brandId}`,
      { status },
      () => api.get(`/api/seo/content/${brandId}`, { params: status ? { status } : {} }),
      CACHE_TTL.DAY
    ),

  // Expert RP - AI PR recommendations (DAY cache — expensive AI call, refreshed by cron/Actualiser only)
  getExpertRP: (brandId, forceRefresh = false) =>
    fetchWithCache(
      `seo/expert-rp/${brandId}`, {},
      () => api.post(`/api/seo/expert-rp/${brandId}`, null, {
        timeout: 120000  // 2 minutes for AI analysis
      }),
      CACHE_TTL.DAY,
      forceRefresh
    ),
};

// === Missions API ===
export const missionsAPI = {
  getDefinitions: (profileType, sector) => {
    const params = new URLSearchParams();
    if (profileType) params.append('profile_type', profileType);
    if (sector) params.append('sector', sector);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return fetchWithCache(
      `missions/definitions/${profileType || 'all'}/${sector || 'all'}`,
      {},
      () => api.get(`/api/missions/definitions${qs}`),
      CACHE_TTL.DAY
    );
  },

  getKpiMapping: () =>
    fetchWithCache('missions/kpi-mapping', {}, () => api.get('/api/missions/kpi-mapping'), CACHE_TTL.DAY),

  getBrandMissions: (brandId) =>
    fetchWithCache(
      `missions/brand/${brandId}`,
      {},
      () => api.get(`/api/missions/brand/${brandId}`),
      CACHE_TTL.DAY
    ),

  setMissions: (brandId, missions, targetConfigs = null) => {
    const body = { missions };
    if (targetConfigs && Object.keys(targetConfigs).length > 0) {
      body.target_configs = targetConfigs;
    }
    return api.post(`/api/missions/brand/${brandId}`, body).then(response => {
      invalidateBrandCache(brandId);
      return response;
    });
  },

  getProgress: (brandId, forceRefresh = false) =>
    fetchWithCache(
      `missions/progress/${brandId}`,
      {},
      () => api.get(`/api/missions/brand/${brandId}/progress`),
      CACHE_TTL.DAY,
      forceRefresh
    ),
};

// === Billing API (Stripe subscription management) ===
export const billingAPI = {
  getSubscription: () => api.get('/api/billing/subscription'),
  createCheckout: (data) => api.post('/api/billing/checkout', data),
  openPortal: (data) => api.post('/api/billing/portal', data),
};

// === Agency API (Multi-tenant team management) ===
export const agencyAPI = {
  getMe: () => fetchWithCache('agency/me', {}, () => api.get('/api/agency/me'), CACHE_TTL.DAY),
  getMyPages: (brandId) => api.get(`/api/agency/me/pages/${brandId}`),
  updateMe: (data) => api.put('/api/agency/me', data).then(res => { invalidateCache('agency/me'); return res; }),
  getUsers: () => api.get('/api/agency/users'),
  inviteUser: (data) => api.post('/api/agency/users/invite', data),
  updateUserRole: (userId, role) => api.put(`/api/agency/users/${userId}/role`, { role }),
  removeUser: (userId) => api.delete(`/api/agency/users/${userId}`),
  getUserBrands: (userId) => api.get(`/api/agency/users/${userId}/brands`),
  setUserBrands: (userId, access) => api.put(`/api/agency/users/${userId}/brands`, { access }),
  resetPassword: (userId) => api.post(`/api/agency/users/${userId}/reset-password`),
  // Brand plans (per-brand tier configuration)
  getDefaultPlans: () => api.get('/api/agency/default-plans'),
  getBrandPlans: () => fetchWithCache('agency/brand-plans', {}, () => api.get('/api/agency/brand-plans'), CACHE_TTL.DAY),
  getBrandPlan: (brandId) => fetchWithCache(`agency/brand-plans/${brandId}`, {}, () => api.get(`/api/agency/brand-plans/${brandId}`), CACHE_TTL.DAY),
  setBrandPlan: (brandId, data) => api.put(`/api/agency/brand-plans/${brandId}`, data).then(res => {
    invalidateCache(`agency/brand-plans/${brandId}`);
    invalidateCache('agency/brand-plans');
    invalidateCache('cockpit/summary');
    return res;
  }),
  // Email configuration status
  getEmailStatus: () => api.get('/api/agency/email-status'),
  testEmail: () => api.post('/api/agency/test-email'),
};

// === Cockpit API (Agency admin dashboard) ===
export const cockpitAPI = {
  getSummary: (period = '30d') => fetchWithCache(
    `cockpit/summary/${period}`, {},
    () => api.get('/api/agency/cockpit/summary', { params: { period } }),
    CACHE_TTL.DAY
  ),
};

// Superadmin API (Refonte) — all GETs cached DAY (24h)
export const superadminAPI = {
  getAgencies: () => fetchWithCache(
    'v2/superadmin/agencies', {},
    () => api.get('/api/v2/superadmin/agencies'),
    CACHE_TTL.DAY
  ),
  getAgency: (id) => fetchWithCache(
    `v2/superadmin/agency/${id}`, {},
    () => api.get(`/api/v2/superadmin/agencies/${id}`),
    CACHE_TTL.DAY
  ),
  updateAgency: (id, data) => api.patch(`/api/v2/superadmin/agencies/${id}`, data).then(res => {
    invalidateCache('v2/superadmin/agencies');
    invalidateCache('v2/superadmin/cockpit');
    return res;
  }),
  getUsers: () => fetchWithCache(
    'v2/superadmin/users', {},
    () => api.get('/api/v2/superadmin/users'),
    CACHE_TTL.DAY
  ),
  updateUser: (id, data) => api.patch(`/api/v2/superadmin/users/${id}`, data).then(res => {
    invalidateCache('v2/superadmin/users');
    invalidateCache('v2/superadmin/agencies');
    return res;
  }),
  resetPassword: (id) => api.post(`/api/v2/superadmin/users/${id}/reset-password`),
  getMetrics: () => fetchWithCache(
    'v2/superadmin/metrics', {},
    () => api.get('/api/v2/superadmin/metrics'),
    CACHE_TTL.DAY
  ),
  getAiUsage: (days) => fetchWithCache(
    'v2/superadmin/ai-usage', { days: days || 30 },
    () => api.get(`/api/v2/superadmin/ai-usage?days=${days || 30}`),
    CACHE_TTL.DAY
  ),
  createAgency: (data) => api.post('/api/v2/superadmin/agencies', data).then(res => {
    invalidateCache('v2/superadmin/agencies');
    invalidateCache('v2/superadmin/cockpit');
    return res;
  }),
  createUser: (agencyId, data) => api.post(`/api/v2/superadmin/agencies/${agencyId}/users`, data).then(res => {
    invalidateCache('v2/superadmin/agencies');
    return res;
  }),
  getPricing: () => fetchWithCache(
    'v2/superadmin/pricing', {},
    () => api.get('/api/v2/superadmin/pricing'),
    CACHE_TTL.DAY
  ),
  updatePricing: (data) => api.put('/api/v2/superadmin/pricing', data).then(res => {
    invalidateCache('v2/superadmin/pricing');
    invalidateCache('v2/superadmin/cockpit');
    return res;
  }),
  getCockpit: () => fetchWithCache(
    'v2/superadmin/cockpit', {},
    () => api.get('/api/v2/superadmin/cockpit'),
    CACHE_TTL.DAY
  ),
  getAuditLogs: (days = 30, action = null, agencyId = null) => fetchWithCache(
    'v2/superadmin/audit/logs', { days, action, agencyId },
    () => api.get('/api/v2/superadmin/audit/logs', {
      params: { days, ...(action && { action }), ...(agencyId && { agency_id: agencyId }) },
    }),
    CACHE_TTL.DAY
  ),
};

// Export default api instance for custom calls
export default api;
